/**
 * Marketing / GA4 data-layer event names pushed via marketingTracking.ts.
 *
 * Centralized so GTM tag triggers, funnel.config.ts, and SPA pushes stay in sync.
 */

/** SPA route change — consumed by GA4 page_view tag in GTM. */
export const MARKETING_EVENT_VIRTUAL_PAGEVIEW = 'virtual_pageview';

/** Public Shop menu opened (desktop Menu or mobile accordion). */
export const MARKETING_EVENT_SHOP_MENU_OPENED = 'shop_menu_opened';

/** Public Shop product link clicked (nav or footer). */
export const MARKETING_EVENT_SHOP_PRODUCT_CLICKED = 'shop_product_clicked';

/**
 * Public social follow link clicked (footer → Instagram/Facebook).
 * Outbound intent only — does not replace inbound UTM attribution from Meta posts.
 */
export const MARKETING_EVENT_SOCIAL_FOLLOW_CLICKED = 'social_follow_clicked';

/** Verified Stripe subscription conversion. */
export const MARKETING_EVENT_SUBSCRIPTION_PURCHASE = 'subscription_purchase';

/** Free-plan onboarding-complete conversion. */
export const MARKETING_EVENT_FREE_SIGNUP = 'free_signup';

/**
 * Marketing cookies granted (banner Accept All, preferences save, or restore
 * from localStorage). Non-Google tags that use GTM "Require additional consent"
 * do not re-fire when Consent Mode later flips to granted — Meta Pixel / ChatGPT
 * Ads base tags load on this event instead of All Pages so the first hit happens
 * after ad_storage is already granted.
 */
export const MARKETING_EVENT_MARKETING_CONSENT_GRANTED = 'marketing_consent_granted';

/**
 * Marketing cookies revoked after a prior grant this page load (preferences save
 * or Essential Only). ChatGPT Ads Custom HTML calls oaiq("consent", false) on
 * this event so measurement stops without requiring a full page reload.
 */
export const MARKETING_EVENT_MARKETING_CONSENT_DENIED = 'marketing_consent_denied';

/** AppModal opened (funnel entry). */
export const MARKETING_EVENT_MODAL_OPEN = 'modal_open';

/** AppModal closed (abandonment or completion signal). */
export const MARKETING_EVENT_MODAL_CLOSED = 'modal_closed';

/** Multi-step wizard step viewed (within-modal drop-off). */
export const MARKETING_EVENT_MODAL_STEP_VIEWED = 'modal_step_viewed';

/**
 * Public "Start for Free" / signup CTA clicked (pre-Entra intent).
 * Not used for paid campaign CTAs — those use campaign_cta_clicked.
 */
export const MARKETING_EVENT_SIGNUP_CTA_CLICKED = 'signup_cta_clicked';

/**
 * User successfully accepted Terms via /accept-terms (post-Entra, pre-app).
 */
export const MARKETING_EVENT_TERMS_ACCEPTED = 'terms_accepted';

/** Welcome onboarding overlay first shown (signup funnel entry). */
export const MARKETING_EVENT_ONBOARDING_STARTED = 'onboarding_started';

/** Onboarding completed (free or paid plan). */
export const MARKETING_EVENT_ONBOARDING_COMPLETED = 'onboarding_completed';

/** Contact successfully created via API. */
export const MARKETING_EVENT_CONTACT_CREATED = 'contact_created';

/** User chose a service at the smile-choice crossroads. */
export const MARKETING_EVENT_SERVICE_SELECTED = 'service_selected';

/** Any schedulable service successfully created via API (e-card, flowers, mailed card). */
export const MARKETING_EVENT_SERVICE_SCHEDULED = 'service_scheduled';

/** Flower or mailed-card pre-order created (GA4 purchase + remarketing; GMV only). */
export const MARKETING_EVENT_ORDER_SCHEDULED = 'order_scheduled';

/** Public flower or mailed-card PDP viewed (GA4 view_item + dynamic remarketing). */
export const MARKETING_EVENT_VIEW_ITEM = 'view_item';

/**
 * Shop product checkout intent (GA4 begin_checkout + remarketing).
 * Fires when the flower wizard reaches review with a product — not on modal open or edit mode.
 */
export const MARKETING_EVENT_BEGIN_CHECKOUT = 'begin_checkout';

/** Seasonal marketing banner shown on the public landing page. */
export const MARKETING_EVENT_SEASONAL_BANNER_SHOWN = 'seasonal_banner_shown';

/** Seasonal marketing banner CTA clicked. */
export const MARKETING_EVENT_SEASONAL_BANNER_CLICKED = 'seasonal_banner_clicked';

/** Paid campaign landing page viewed (/autopilot). */
export const MARKETING_EVENT_CAMPAIGN_PAGE_VIEWED = 'campaign_page_viewed';

/** Primary CTA clicked on a paid campaign landing page. */
export const MARKETING_EVENT_CAMPAIGN_CTA_CLICKED = 'campaign_cta_clicked';

/** Campaign-aware WelcomeOnboarding variant shown. */
export const MARKETING_EVENT_CAMPAIGN_ONBOARDING_VIEWED = 'campaign_onboarding_viewed';

/**
 * Stripe Embedded Checkout about to show for a paid campaign claim.
 * Observation / funnel only — never a Google Ads Primary conversion.
 */
export const MARKETING_EVENT_CAMPAIGN_CHECKOUT_STARTED = 'campaign_checkout_started';

/** Catalog identifier kind on service_scheduled (non-PII). */
export type ServiceCatalogKind = 'card_template' | 'flower_sku' | 'mailed_card_artwork';

/** Onboarding completion plan breakdown for GA4 funnels. */
export type OnboardingPlan = 'free' | 'paid';

/**
 * Maps AppModal ids to ServiceType for funnel filters.
 * Keep in sync with infrastructure/MARKETING-TAGS.md.
 */
export const MODAL_ID_TO_SERVICE_TYPE = {
  'schedule-card': 'ECard',
  'flower-order': 'Flowers',
  'mailed-card-order': 'MailedCard',
} as const;

export type ServiceModalId = keyof typeof MODAL_ID_TO_SERVICE_TYPE;
